// src/pages/organization/dashboard.js

import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMainPageTitle } from "../../contexts/PageContext";

import SubscriptionUsageChart from '../../components/organization/dashboard/SubscriptionUsageChart';

const Dashboard = () => {
  const { t } = useTranslation();
  const { updatePageTitle } = useMainPageTitle();
  
  // State for dashboard data
  const [usageData, setUsageData] = useState(null);

  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  // Fetch all dashboard data
  const fetchDashboardData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      // Parallel API calls for better performance
      const API_BASE_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1" || 'http://localhost:3000/api/v1';
      console.log('Making API calls to:', API_BASE_URL);
      const [usageResponse, eventsResponse, subscriptionResponse] = await Promise.allSettled([
        fetch(`${API_BASE_URL}/organization/usage`, {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${API_BASE_URL}/organization/events-analytics`, {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${API_BASE_URL}/organization/subscription-analytics`, {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      // Process usage data (our primary data source now)
      if (usageResponse.status === 'fulfilled' && usageResponse.value.ok) {
        const usageResult = await usageResponse.value.json();
        if (usageResult.status === 'success') {
          setUsageData(usageResult);
          console.log('Usage data received:', usageResult);
        } else {
          console.error('Usage API failed:', usageResult);
        }
      } else if (usageResponse.status === 'fulfilled') {
        console.error('Usage API error:', usageResponse.value.status, await usageResponse.value.text());
      } else {
        console.error('Usage API rejected:', usageResponse.reason);
      }

      // Process events analytics
      if (eventsResponse.status === 'fulfilled' && eventsResponse.value.ok) {
        const eventsResult = await eventsResponse.value.json();
        if (eventsResult.status === 'success') {
          // Events data can be used for future enhancements
          console.log('Events data received:', eventsResult);
        } else {
          console.error('Events API failed:', eventsResult);
        }
      } else if (eventsResponse.status === 'fulfilled') {
        console.error('Events API error:', eventsResponse.value.status, await eventsResponse.value.text());
      } else {
        console.error('Events API rejected:', eventsResponse.reason);
      }

      // Process subscription analytics
      if (subscriptionResponse.status === 'fulfilled' && subscriptionResponse.value.ok) {
        const subscriptionResult = await subscriptionResponse.value.json();
        if (subscriptionResult.status === 'success') {
          setSubscriptionData(subscriptionResult);
        } else {
          console.error('Subscription API failed:', subscriptionResult);
        }
      } else if (subscriptionResponse.status === 'fulfilled') {
        console.error('Subscription API error:', subscriptionResponse.value.status, await subscriptionResponse.value.text());
      } else {
        console.error('Subscription API rejected:', subscriptionResponse.reason);
      }



      // Check if all requests failed
      const allFailed = 
        usageResponse.status === 'rejected' && 
        eventsResponse.status === 'rejected' && 
        subscriptionResponse.status === 'rejected';

      if (allFailed && retryCount < maxRetries) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          fetchDashboardData();
        }, 1000 * (retryCount + 1)); // Exponential backoff
        return;
      }

    } catch (error) {
      console.error('Dashboard data fetch error:', error);
      setError('Failed to load dashboard data. Please try again later.');
      
      if (retryCount < maxRetries) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          fetchDashboardData();
        }, 1000 * (retryCount + 1));
      }
    } finally {
      setLoading(false);
    }
  }, [retryCount]);

  useEffect(() => {
    updatePageTitle(t("dashboard.title"), t("dashboard.subtitle"));
    fetchDashboardData();
  }, [updatePageTitle, t, fetchDashboardData]);

  // Retry function for the new data structure
  const retryAll = () => {
    setRetryCount(0);
    fetchDashboardData();
  };

  // Prepare metrics for display using usage data
  const getQuickStats = () => {
    if (!usageData) {
      return [
        { label: t('organization.totalTeams'), value: '-', change: null, icon: '👥' },
        { label: t('organization.totalPlayers'), value: '-', change: null, icon: '⚽' },
        { label: t('organization.totalStaff'), value: '-', change: null, icon: '👨‍💼' },
        { label: t('organization.thisMonthEvents'), value: '-', change: null, icon: '📅' },
        { label: t('organization.thisMonthMatches'), value: '-', change: null, icon: '🏟️' },
        { label: t('organization.thisMonthCompetitions'), value: '-', change: null, icon: '🏆' }
      ];
    }

    return [
      { 
        label: t('organization.totalTeams'), 
        value: usageData.teams || 0,
        change: `${Math.round((usageData.players || 0) / Math.max(usageData.teams || 1, 1) * 10) / 10} ${t('organization.avgPlayersPerTeam')}`,
        icon: '👥' 
      },
      { 
        label: t('organization.totalPlayers'), 
        value: usageData.players || 0,
        change: t('organization.activePlayers'),
        icon: '⚽' 
      },
      { 
        label: t('organization.totalStaff'), 
        value: usageData.staff || 0,
        change: t('organization.supervisorsAndOrganization'),
        icon: '👨‍💼' 
      },
      { 
        label: t('organization.thisMonthEvents'), 
        value: usageData.events || 0,
        change: t('organization.trainingAndEventsCreated'),
        icon: '📅' 
      },
      { 
        label: t('organization.thisMonthMatches'), 
        value: usageData.matches || 0,
        change: t('organization.competitiveMatches'),
        icon: '🏟️' 
      },
      { 
        label: t('organization.thisMonthCompetitions'), 
        value: usageData.competitions || 0,
        change: t('organization.competitionRequestsSent'),
        icon: '🏆' 
      }
    ];
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto text-center pt-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t("dashboard.loadingDashboardData")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Page Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t("dashboard.welcomeBack")}, {localStorage.getItem('userName') || 'Organization'}!
              </h1>
              <p className="text-gray-600 mt-1">
                {t("dashboard.overview")} - {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={retryAll}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                title={t("dashboard.refreshDashboard")}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
          {getQuickStats().map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  {stat.change && (
                    <p className={`text-sm ${
                      stat.change.includes('+') || stat.change.includes('active') 
                        ? 'text-green-600' 
                        : stat.change.includes('-') 
                          ? 'text-red-600' 
                          : 'text-gray-600'
                    }`}>
                      {stat.change}
                    </p>
                  )}
                </div>
                <div className="text-3xl">{stat.icon}</div>
              </div>
            </div>
          ))}
        </div>


        <div className="mt-6">
          <SubscriptionUsageChart 
            data={subscriptionData} 
            loading={loading} 
          />
        </div>

        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{t("dashboard.errorLoadingDashboard")}</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{error}</p>
                  {retryCount < maxRetries && (
                    <button 
                      onClick={() => fetchDashboardData()} 
                      className="mt-2 bg-red-100 hover:bg-red-200 px-3 py-1 rounded text-red-800 text-xs font-medium"
                    >
                      {t("dashboard.retry")} ({retryCount + 1}/{maxRetries})
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
